import * as React from 'react'
import { Popup } from '@/ui/overlays/Popup'
import { PopupStepWebcard } from '@/modules/imports/ui/PopupStepWebcard'
import { StepCommonPending } from '@/modules/imports/container/import-popup-container/StepCommonPending'
import { PopupStepImportingInner } from '@/modules/imports/ui/PopupStepImportingInner'
import { PopupStepImportSummary } from '@/modules/imports/ui/step-import-summary/PopupStepImportSummary'
import { useImportFlow } from '@/modules/imports/container/import-popup-container/useImportFlow'
import {
  getImportStepMessages,
  isPendingStep,
} from '@/modules/imports/container/import-popup-container/config'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { getProgressConfig } from '@/modules/imports/container/import-popup-container/utils'

type Props = {
  isOpen?: boolean
  handleClose?: () => void
  companyId: string
  onSuccess?: () => void
}

/**
 * Import popup that starts with a WebCard reader step instead of a file picker.
 * After the card is read, the DDD bytes are passed as a File into the standard
 * upload → map → confirm flow.
 */
export const WebcardImportPopupContainer = ({
  isOpen,
  handleClose,
  companyId,
  onSuccess,
}: Props) => {
  const { t } = useLocalize()
  const IMPORT_STEP_MESSAGES = getImportStepMessages(t)

  const {
    step,
    handleFinish,
    handleDone,
    handleImport,
    handleUploadFiles,
    handleRestart,
    handleAbortAndReset,
    uploadDetails,
    importResult,
    isImportResultPending,
    importTask,
    filesCount,
    uploadedFilesCount,
    logsCount,
    summarySnapshot,
  } = useImportFlow({
    handleClose,
    companyId,
    onSuccess,
  })

  /** Called by PopupStepWebcard once the card has been read and DDD is ready. */
  const handleDddReady = React.useCallback(
    (dddFile: File) => {
      handleUploadFiles([dddFile])
    },
    [handleUploadFiles]
  )

  const handleCloseOrCancel = () => {
    handleAbortAndReset()
    handleClose?.()
  }

  const isInWebcardStep = step === 'user:uploadFiles'

  return (
    <Popup
      open={isOpen}
      onClose={handleCloseOrCancel}
      zIndexClassName="tw-z-10"
      contentClassName="tw-min-w-[80vw]"
    >
      {/* Step 1: read physical card */}
      {isInWebcardStep && <PopupStepWebcard onDddReady={handleDddReady} />}

      {/* Steps 2-N: identical to the standard import flow */}
      {isPendingStep(step) && (
        <StepCommonPending
          title={IMPORT_STEP_MESSAGES[step]?.title || ''}
          description={IMPORT_STEP_MESSAGES[step]?.description || ''}
          onAbort={handleCloseOrCancel}
          {...getProgressConfig({
            step,
            filesCount,
            uploadedFilesCount,
            logsCount,
          })}
        />
      )}

      {step === 'user:mapAndSelect' && (
        <PopupStepImportingInner
          handleImport={handleImport}
          readModel={uploadDetails}
          companyId={companyId}
          handleCancel={handleRestart}
          handleDone={handleDone}
        />
      )}

      {step === 'user:confirmSummary' && (
        <PopupStepImportSummary
          uploadDetails={uploadDetails}
          importResult={importResult}
          isResultPending={isImportResultPending}
          task={importTask}
          handleSubmit={handleFinish}
          summarySnapshot={summarySnapshot}
        />
      )}
    </Popup>
  )
}
